import React from "react";
import { cn } from "../../DashboardPage/lib/utils";

export interface FilterPillItem {
  /** Value sent to the caller's toggle handler. */
  key: string;
  label: React.ReactNode;
  /**
   * Live count for this filter. Omit entirely when the real number is not
   * available — never pass a placeholder or a locally-derived approximation.
   */
  count?: number;
}

interface FilterPillsProps {
  items: FilterPillItem[];
  /** Keys currently active. Multi-select: several may be active at once. */
  active: string[];
  onToggle: (key: string) => void;
  /** Renders a leading "All" pill that clears the selection when clicked. */
  allLabel?: React.ReactNode;
  onClearAll?: () => void;
  allCount?: number;
  /**
   * Decoration rendered before the first pill (e.g. a funnel icon). It scrolls
   * with the pills by design - a fixed-width sibling outside the scroller eats
   * space the pills need on narrow screens.
   */
  leading?: React.ReactNode;
  /** Applied to the outer wrapper (vertical padding, margins). */
  className?: string;
  /**
   * Horizontal padding for the scrolling row itself. Kept separate from
   * `className` because it must live on the scroller - put it on the wrapper
   * and the right-edge fade is inset by that much, leaving a gap where pills
   * still clip hard against the border.
   */
  insetClassName?: string;
}

const pillClass = (active: boolean) =>
  cn(
    // shrink-0 + whitespace-nowrap so pills keep their shape when the row is
    // scrolling sideways rather than wrapping.
    "border inline-flex shrink-0 whitespace-nowrap items-center gap-1.5 px-3 py-1.5 text-sm rounded-full transition-colors focus:outline-none focus-visible:border-primary-700",
    active
      ? // Active reads as a filled brand chip, not just tinted text, so the
        // current filter is obvious at a glance on a busy table header.
        "bg-primary-700 border-primary-700 text-white font-medium shadow-sm"
      : "bg-white border-neutral-200 text-neutral-600 hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700"
  );

// On an active (filled) pill the count sits on primary-700, so it needs a light
// treatment rather than the brand colour it used when the pill was tinted.
const countClass = (active: boolean) =>
  cn(
    "text-xs tabular-nums",
    active ? "text-white/75" : "text-neutral-400"
  );

/**
 * Quick-filter chips with an optional live count. Multi-select — clicking a
 * pill toggles it rather than replacing the selection.
 */
export default function FilterPills({
  items,
  active,
  onToggle,
  allLabel,
  onClearAll,
  allCount,
  leading,
  className,
  insetClassName = "px-3",
}: FilterPillsProps) {
  const noneActive = active.length === 0;

  return (
    /*
     * `relative` anchors the right-edge fade below; the fade is what tells the
     * user there are more pills off-screen. Without it a pill clipped by the
     * card edge just looks like a truncated word.
     */
    <div className={cn("relative", className)}>
      {/*
       * Single scrolling row, never wrapping. The pills are shrink-0 /
       * whitespace-nowrap, so `flex-wrap` here broke them onto a second line on
       * narrow screens and left the caller's leading icon floating beside a
       * two-line block. Sideways scroll keeps the filter bar one row tall at any
       * width; `scrollbar-hide` matches Tabs/EmailTabs elsewhere in the app.
       */}
      <div
        className={cn(
          "flex items-center gap-2 overflow-x-auto scrollbar-hide",
          insetClassName
        )}
      >
        {leading}
        {allLabel && onClearAll && (
          <button
            type="button"
            onClick={onClearAll}
            aria-pressed={noneActive}
            className={pillClass(noneActive)}
          >
            <span>{allLabel}</span>
            {allCount != null && (
              <span className={countClass(noneActive)}>{allCount}</span>
            )}
          </button>
        )}
        {items.map((item) => {
          const isActive = active.includes(item.key);
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => onToggle(item.key)}
              aria-pressed={isActive}
              className={pillClass(isActive)}
            >
              {/* No text-transform: labels are rendered exactly as supplied, so
                  callers control casing (e.g. "Re-delivery", not "Re-Delivery"). */}
              <span>{item.label}</span>
              {item.count != null && (
                <span className={countClass(isActive)}>{item.count}</span>
              )}
            </button>
          );
        })}
      </div>

      {/*
       * Affordance for the sideways scroll: pills pass under a short fade at
       * the right edge instead of being cut dead against the card border.
       * Purely decorative, so it never intercepts taps.
       */}
      <div
        aria-hidden="true"
        className="absolute inset-y-0 right-0 w-8 pointer-events-none bg-gradient-to-l from-white via-white/80 to-transparent"
      />
    </div>
  );
}
